import { ArtifactEditor } from "@/components/artifacts/ArtifactEditor.tsx";
import { iconForName } from "@/components/artifacts/iconForName.ts";
import { useArtifactViewCtx } from "@/components/artifacts/view/ArtifactViewContext.tsx";
import { Input } from "@/components/ui/input.tsx";
import { errorToast } from "@/components/ui/toast.tsx";
import type { ArtifactComponent } from "@/types/artifacts-view.ts";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils.ts";

type TextComponentData = Extract<ArtifactComponent, { type: "text" }>;

export const SubjectLineBlock = ({ component }: { component: TextComponentData }) => {
  const { isReadOnly, patchOne } = useArtifactViewCtx();
  const readOnly = isReadOnly || !component.editable;

  const save = async (next: string) => {
    if (!component.editPath) return;
    await patchOne(component.editPath, next);
  };

  const Icon = component.icon ? iconForName(component.icon) : null;

  // Both heading variants render as static heading text when read-only
  // (file name, OAuth title, received-comm subject) — mounting an editor for a
  // non-editable string is heavyweight and can mis-parse markdown-ish values
  // (e.g. "report_v2.md").
  const isHeading = component.variant === "headline" || component.variant === "title";
  if (isHeading && readOnly) {
    return (
      <div className="flex items-center gap-2 font-semibold text-base">
        {Icon ? <Icon className="size-5" /> : null}
        {component.value}
      </div>
    );
  }

  // `"headline"` => rich-text editor (values that may carry inline formatting).
  if (component.variant === "headline") {
    return (
      <ArtifactEditor
        content={component.value}
        readOnly={false}
        handleSave={save}
        hideToolbar
        contentEditableClassName="font-semibold"
      />
    );
  }

  // `"title"` => heading-styled single-line input for plain names (no rich
  // text). `"plain"` => bare inline input.
  return (
    <SubjectInput
      label={component.label}
      value={component.value}
      readOnly={readOnly}
      onSave={save}
      headingStyle={component.variant === "title"}
      boxed={component.boxed ?? false}
    />
  );
};

const SubjectInput = ({
  label,
  value,
  readOnly,
  onSave,
  headingStyle = false,
  boxed = false,
}: {
  label?: string;
  value: string;
  readOnly: boolean;
  onSave: (next: string) => Promise<void>;
  /** Render the input as heading text (bold, larger) for title-style names. */
  headingStyle?: boolean;
  /** Render as a bordered form input with the label stacked above. */
  boxed?: boolean;
}) => {
  const [draft, setDraft] = useState(value);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!dirty) setDraft(value);
  }, [value, dirty]);

  const onBlur = async () => {
    if (!dirty || draft === value) {
      setDirty(false);
      return;
    }
    try {
      await onSave(draft);
      // Only clear `dirty` after a successful save so a failed save doesn't
      // let the resync effect revert the user's edit.
      setDirty(false);
    } catch (error) {
      console.error(error);
      errorToast("Couldn't save changes");
    }
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDraft(e.target.value);
    setDirty(true);
  };

  if (boxed) {
    // Label on the left (matching the select/number-input rows), bordered
    // input on the right — a compact inline form field.
    return (
      <div className="flex items-center gap-2">
        {label ? (
          <span className="text-xs text-muted-foreground w-28 shrink-0">{label}</span>
        ) : null}
        <Input
          type="text"
          value={draft}
          onChange={onChange}
          onBlur={onBlur}
          readOnly={readOnly}
          className={cn("flex-1 min-w-0", readOnly && "cursor-default opacity-90")}
        />
      </div>
    );
  }

  return (
    // `text-sm` on the row so the label takes the row's scale rather than
    // inheriting the body's, which left this key a size larger than the ones
    // above it and than its own value. The input sets its own size, so a
    // title-variant heading is unaffected — it reads as a heading by weight,
    // since this app's `text-base` and `text-sm` are the same 14px.
    <div className="flex flex-row gap-2 items-center text-sm">
      {label ? <div className="font-medium shrink-0">{label}</div> : null}
      <input
        type="text"
        value={draft}
        onChange={onChange}
        onBlur={onBlur}
        readOnly={readOnly}
        className={cn(
          "flex-1 bg-transparent outline-none min-w-0",
          headingStyle ? "text-base font-semibold" : "text-sm",
          readOnly && "cursor-default",
        )}
      />
    </div>
  );
};
